"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Gauge, PhoneCall, Pipette, ShieldAlert, Volume2, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function WarningSigns() {
  const shouldReduceMotion = useReducedMotion();
  const warningList = [{
    icon: Droplets,
    title: "Constant Dripping & Leaks",
    severity: "High Priority",
    description: "A faucet dripping once per second wastes hundreds of gallons per month and signals worn O-rings, damaged cartridges, or valve seat erosion.",
    actionNote: "Can double your water bill and cause premature fixture failure."
  }, {
    icon: Gauge,
    title: "Sudden Drop in Water Pressure",
    severity: "Moderate to Severe",
    description: "Weak stream flow points to severe mineral buildup, a clogged aerator, or hidden pipe blockages inside the wall supply lines.",
    actionNote: "May indicate a hidden leak or main line pressure issue."
  }, {
    icon: ShieldAlert,
    title: "Water Under the Vanity or Cabinet",
    severity: "Immediate Emergency",
    description: "Moisture, swelling cabinet baseboards, or standing puddles under the sink show loose drain slip joints or leaking supply hoses.",
    actionNote: "Leads to mold growth, rotted wood subflooring, and structural rot."
  }, {
    icon: Waves,
    title: "Slow Drain with Gurgling Sounds",
    severity: "Urgent",
    description: "Water draining at a crawl combined with bubbling noises indicates a clogged P-trap or blocked plumbing vent stack.",
    actionNote: "Pushes foul sewer gases back into your bathroom or kitchen."
  }, {
    icon: Pipette,
    title: "Rust, Oxidation & Discolored Water",
    severity: "High Priority",
    description: "Brown or metallic-smelling water when opening the tap points to corroded internal pipes or failing shutoff valves.",
    actionNote: "Debris can contaminate drinking water and ruin expensive fixtures."
  }, {
    icon: Volume2,
    title: "Screeching or Clunking Pipes",
    severity: "Moderate",
    description: "Loud thumps (water hammer) or whistling noises when you turn the faucet handle reveal loose pipe strapping or high water pressure.",
    actionNote: "Excessive pressure can burst flexible supply lines without warning."
  }];
  return <section id="sinkfaucetwarningsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Jonesboro Diagnostic Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Signs Your Sink or Faucet Needs Professional Attention
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Minor drips and hidden moisture can quickly escalate into costly water damage.
            If you notice any of these warning signs in your Jonesboro home, our emergency plumbers are ready 24/7.
          </p>
        </div>

        {/* Warning Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {warningList.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.08
          }}>
                <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs font-semibold">
                        {item.severity}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-0">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                    <div className="rounded-md bg-muted/60 p-3 text-xs text-foreground font-medium border border-border/50">
                      <strong className="text-primary font-semibold">Risk: </strong>
                      {item.actionNote}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Emergency Alert & Call to Action Bar */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8 space-y-3">
              <Alert variant="default" className="border-accent/40 bg-accent/10 mb-2">
                <AlertTriangle className="size-4 text-accent-foreground" />
                <AlertTitle className="text-accent-foreground font-semibold">
                  Active leak flooding under your sink?
                </AlertTitle>
                <AlertDescription className="text-foreground/80">
                  Shut off the angle stop valve under the basin immediately and reach out for fast local dispatch.
                </AlertDescription>
              </Alert>
              <h3 className="text-2xl font-bold font-default text-card-foreground">
                Get Same-Day Sink & Faucet Repairs in Jonesboro, GA
              </h3>
              <p className="text-sm text-muted-foreground">
                Our licensed emergency technicians carry standard replacement cartridges, valves, and traps on every truck for rapid one-visit repairs.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
              <Button asChild size="lg" className="w-full">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  Request Service Now
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full">
                <Link to="/services/" className="flex items-center justify-center gap-2">
                  View All Services
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}